import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type JSX,
} from 'react';
import { normalizeRole, type TRole } from '../auth/roles';
import { useUser } from './UserProvider';

interface IAuthorizationProviderProps {
  children: React.ReactNode;
}

interface IAuthorizationContextValue {
  /** The current user's role, or `null` when logged out. */
  role: TRole | null;
  /** True while auth state or the role claim is still resolving. */
  isLoading: boolean;
  /** Convenience flag: a user is signed in. */
  isAuthenticated: boolean;
  /**
   * Force-refresh the ID token and re-read the role claim. Call this after the
   * acting user changes their OWN role, since custom claims are otherwise only
   * refreshed when the token rotates (~1h).
   */
  refreshRole: () => Promise<void>;
}

const AuthorizationContext = createContext<IAuthorizationContextValue>({
  role: null,
  isLoading: true,
  isAuthenticated: false,
  refreshRole: async () => {},
});

/**
 * Resolves the current user's role from their Firebase Auth custom claims.
 *
 * Kept separate from {@link UserProvider} (Single Responsibility): role
 * resolution requires an async `getIdTokenResult()` with its own loading and
 * force-refresh lifecycle that should not block the many consumers of
 * `useUser()`. Must be rendered inside `UserProvider`.
 */
export function AuthorizationProvider({
  children,
}: Readonly<IAuthorizationProviderProps>): JSX.Element {
  const { user, isLoading: isUserLoading } = useUser();
  const [role, setRole] = useState<TRole | null>(null);
  const [isRoleLoading, setIsRoleLoading] = useState(true);

  const resolveRole = useCallback(
    async (forceRefresh: boolean): Promise<void> => {
      if (!user) {
        setRole(null);
        setIsRoleLoading(false);
        return;
      }
      try {
        const result = await user.getIdTokenResult(forceRefresh);
        setRole(normalizeRole(result.claims.role));
      } catch {
        // On failure to read the token, treat as the default (least-privileged) role.
        setRole(normalizeRole(undefined));
      } finally {
        setIsRoleLoading(false);
      }
    },
    [user]
  );

  useEffect(() => {
    setIsRoleLoading(true);
    void resolveRole(false);
  }, [resolveRole]);

  const refreshRole = useCallback(async (): Promise<void> => {
    await resolveRole(true);
  }, [resolveRole]);

  // Memoized so context consumers only re-render when the role or loading state
  // actually changes — not on every provider render.
  const value = useMemo<IAuthorizationContextValue>(
    () => ({
      role,
      isLoading: isUserLoading || isRoleLoading,
      isAuthenticated: !!user,
      refreshRole,
    }),
    [role, isUserLoading, isRoleLoading, user, refreshRole]
  );

  return <AuthorizationContext.Provider value={value}>{children}</AuthorizationContext.Provider>;
}

export function useAuthorizationContext(): IAuthorizationContextValue {
  return useContext(AuthorizationContext);
}

export { AuthorizationContext };
export type { IAuthorizationContextValue };
